import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import {
  type BoardViewCollapseSlice,
  createBoardViewCollapseSlice,
} from '~/components/v2/store/shared/createBoardViewCollapseSlice';
import { createDraftCanvasSlice, type DraftCanvasSlice } from '~/components/v2/store/shared/createDraftCanvasSlice';
import { createPanelSlice, type PanelSlice } from '~/components/v2/store/shared/createPanelSlice';
import {
  createWorkspaceRightPanelCanvasSlice,
  type WorkspaceRightPanelCanvasSlice,
} from '~/components/v2/store/shared/createWorkspaceRightPanelCanvasSlice';
import {
  createWorkspaceRightPanelFullscreenSlice,
  type WorkspaceRightPanelFullscreenSlice,
} from '~/components/v2/store/shared/createWorkspaceRightPanelFullscreenSlice';
import {
  createWorkspaceRightPanelWidthSlice,
  type WorkspaceRightPanelWidthSlice,
} from '~/components/v2/store/shared/createWorkspaceRightPanelWidthSlice';
import { QueryParamNames } from '~/constants/search';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { getInitialRightPanelWidth } from '~/utils/getInitialRightPanelWidth';
import { syncRightPanelWidthToCSS } from '~/utils/syncRightPanelWidthToCSS';

/** Persisted store version. Bumped to 7 when boardViewCollapsed was added. */
const WORKSPACE_RIGHT_PANEL_STORE_VERSION = 7;

export type WorkspaceRightPanelState = PanelSlice &
  WorkspaceRightPanelWidthSlice &
  WorkspaceRightPanelCanvasSlice &
  WorkspaceRightPanelFullscreenSlice &
  BoardViewCollapseSlice &
  DraftCanvasSlice;

export const useWorkspaceRightPanelStore = create<WorkspaceRightPanelState>()(
  persist(
    (set, get, store) => ({
      ...createPanelSlice({ open: false })(set, get, store),
      ...createWorkspaceRightPanelWidthSlice({
        getInitialWidth: getInitialRightPanelWidth,
        syncWidthToCSS: syncRightPanelWidthToCSS,
      })(set, get, store),
      ...createWorkspaceRightPanelCanvasSlice()(set, get, store),
      ...createWorkspaceRightPanelFullscreenSlice()(set, get, store),
      ...createBoardViewCollapseSlice()(set, get, store),
      ...createDraftCanvasSlice()(set, get, store),
      closeFullscreen: () => set({ isFullscreen: false, open: false }),
    }),
    {
      name: ZustandStorageKeys.WorkspaceRightPanel,
      partialize: (state) => ({
        open: state.open,
        width: state.width,
        selectedCanvasId: state.selectedCanvasId,
        boardViewCollapsed: state.boardViewCollapsed,
      }),
      merge: (persistedState, currentState) => {
        const safePersisted = persistedState && typeof persistedState === 'object' ? persistedState : {};

        // Deep-link create (canvasName in URL) must start without a rehydrated canvas
        // selection. Otherwise useSelectedCanvasIdSync's store→URL effect pushes the
        // stale cnId into the URL, which then races the new-canvas flow:
        // useCreateCanvasFromSearchParams' commit-time URL strip clears cnId, the
        // URL→store effect interprets that as a panel close, and the close cascade
        // discards the in-progress draft — silently killing the deep-link.
        const search = typeof window !== 'undefined' ? window.location.search : '';
        const isDeepLinkCreate = new URLSearchParams(search).has(QueryParamNames.canvasName);
        const filteredPersisted = isDeepLinkCreate
          ? Object.fromEntries(
              Object.entries(safePersisted).filter(([key]) => key !== 'selectedCanvasId' && key !== 'open'),
            )
          : safePersisted;

        const merged = { ...currentState, ...filteredPersisted };
        // draftCanvas is intentionally excluded from persistence, so open=true +
        // selectedCanvasId=null after rehydration means the user was in an uncommitted
        // draft before page refresh.

        if (merged.open && !merged.selectedCanvasId) {
          merged.open = false;
        }

        // Don't rehydrate a collapsed board view when the canvas isn't open — the only way to
        // bring the board view back (the title-bar board chip) lives in the canvas-open UI.
        if (!merged.open) {
          merged.boardViewCollapsed = false;
        }

        return merged;
      },
      migrate: (persistedState, version) => {
        const state = (persistedState ?? {}) as Partial<WorkspaceRightPanelState>;

        return {
          open: state.open ?? false,
          width: state.width ?? getInitialRightPanelWidth(),
          selectedCanvasId: state.selectedCanvasId ?? null,
          boardViewCollapsed:
            version < WORKSPACE_RIGHT_PANEL_STORE_VERSION ? false : (state.boardViewCollapsed ?? false),
        };
      },
      version: WORKSPACE_RIGHT_PANEL_STORE_VERSION,
    },
  ),
);

// Sync CSS variable when store rehydrates
useWorkspaceRightPanelStore.subscribe((state) => {
  syncRightPanelWidthToCSS(state.width);
});

// Initialize CSS variable on module load (client-side only)
if (typeof window !== 'undefined') {
  syncRightPanelWidthToCSS(getInitialRightPanelWidth());
}
